import { WarningFilled } from '@ant-design/icons';
import { Col, Divider, Flex, Row, Tag, Tooltip } from 'antd';
import React, { useEffect, useState } from 'react';
import { CrossIcon } from '../../../../assets/icons/icons.jsx';
import { truncateByLength } from '../../../../common/helper';
import {
  AlertsNavButtons,
  CustomShipmentText,
  CustomTitle,
  StatusCardNotification,
  TeamBadge,
} from '../../../../components';
import ShipmentVitals_CT from '../../../Shipments/partials/ShipmentVitals_CT';
import '../../Home.scss';
import theme from '@/assets/theme';
import { SingleShipmentDataExtended, ShipmentAlertData } from '@/apis/types';
import { BeaconEventType } from '@/common/enum';
import {
  buildStatusPillStyle,
  getDeliveryStatusPillColors,
} from '@/pages/Shipments/statusTagColors';
import { useBreakpoints } from '@/hooks/useBreakpoints.js';
import { formatTimeToAct } from '../needsAttention';
import { getAlertColors } from '@/pages/Shipments/components/ShipmentAlerts.js';
import { trackEvent } from '@/hooks';

interface HomeSingleShipmentDataProps {
  shipmentData?: Partial<SingleShipmentDataExtended>;
  alertsData?: ShipmentAlertData[];
  closeModal: () => void;
  goToShipmentDetail: () => void;
  variant?: 'overlay' | 'panel';
  /**
   * TV monitor: why the shipment is on screen and nothing the trip panel
   * already says. No actions, no time-to-act banner, no status, destination
   * or dates; the provider and tracking ID on one line.
   */
  compact?: boolean;
  /**
   * TV monitor with no trip panel (its request failed): keep the status,
   * dates and destination on the card after all.
   */
  showTripFallback?: boolean;
}

/**
 * Modal component displaying detailed shipment information with alerts.
 * Shows shipment data including delivery status, location, courier info, product details,
 * and sensor information. Supports alert navigation when multiple alerts are present.
 *
 * @param shipmentData - Shipment information to display
 * @param alertsData - Array of alerts associated with the shipment
 * @param closeModal - Callback to close the modal
 * @param goToShipmentDetail - Callback to navigate to full shipment detail page
 */
const HomeSingleShipmentData: React.FC<HomeSingleShipmentDataProps> = ({
  shipmentData,
  alertsData,
  closeModal,
  goToShipmentDetail,
  variant = 'overlay',
  compact = false,
  showTripFallback = false,
}) => {
  // On the TV the trip panel says where and when; without it, the card does.
  const showTripRows = !compact || showTripFallback;
  const [alertIndex, setAlertIndex] = useState<number>(0);
  const deliveryStatusColors = getDeliveryStatusPillColors(
    shipmentData?.delivery_status
  );
  // Time-to-act: lead with how long until this shipment arrives (or how
  // overdue it is) so urgency is visible before any status pill.
  const timeToAct = formatTimeToAct(shipmentData ?? null);

  const { mb } = useBreakpoints();

  // Track the active alert becoming visible, same as the shipment detail
  // drawer's ShipmentAlerts. Keyed on the alert's own id rather than the
  // alertsData reference, so a background poll refreshing it doesn't
  // re-log the same alert that's already on screen.
  const activeAlertId = alertsData?.[alertIndex]?.id;
  const shipmentId = shipmentData?.id;
  useEffect(() => {
    if (!activeAlertId || !shipmentId) return;
    trackEvent(BeaconEventType.AlertViewed, {
      reference_id: activeAlertId,
      shipment_id: shipmentId,
    });
  }, [activeAlertId, shipmentId]);

  return (
    <div
      onClick={variant === 'overlay' ? closeModal : undefined}
      className={
        variant === 'overlay'
          ? 'overlay-transparent home-map-detail-wrapper'
          : 'home-map-detail-panel-wrapper'
      }
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`custom-card-drawer home-map ${variant === 'panel' ? 'home-map-right-panel home-map-compact-panel' : ''}`}
      >
        <Flex vertical={true} gap={14} style={{ width: '100%' }}>
          <Flex
            style={{
              justifyContent: 'space-between',
              display: 'flex',
              alignItems: 'center',
            }}
            gap={0}
            vertical={false}
            className='custom-card-drawer-header'
          >
            <Flex align={'center'} gap={8} vertical={false}>
              <div title={shipmentData?.reference_number}>
                <Tooltip title={shipmentData?.reference_number} placement='top'>
                  <span>
                    <CustomTitle
                      text={
                        truncateByLength(
                          shipmentData?.reference_number,
                          mb ? 7 : 17
                        ) || 'N/A'
                      }
                      fontSize={15}
                      fontWeight={700}
                      lineHeight='24px'
                      color='var(--text-primary)'
                      marginBottom='0'
                      marginTop={0}
                    />
                  </span>
                </Tooltip>
              </div>
              {shipmentData?.teams?.length > 0 &&
                (() => {
                  const nonDefaultTeams = shipmentData.teams.filter(
                    (team) => team.name.toLowerCase() !== 'default'
                  );

                  if (nonDefaultTeams.length === 0) return null;

                  return (
                    <div className='flex white-space-nowrap'>
                      <TeamBadge
                        className='mr-1'
                        color={nonDefaultTeams[0]?.color}
                        text={nonDefaultTeams[0]?.name}
                      />
                      {nonDefaultTeams.length > 1 && (
                        <Tooltip
                          title={nonDefaultTeams
                            .slice(1)
                            .map((team) => team.name)
                            .join(', ')}
                          placement='top'
                        >
                          <div
                            style={{
                              display: 'inline-block',
                              cursor: 'pointer',
                            }}
                          >
                            <TeamBadge
                              color={null}
                              text={`+ ${nonDefaultTeams.length - 1}`}
                            />
                          </div>
                        </Tooltip>
                      )}
                    </div>
                  );
                })()}
            </Flex>

            <div
              className='flex-center shipment-data-card-actions'
              style={compact ? { display: 'none' } : undefined}
            >
              <button
                type='button'
                className='shipment-data-card-action'
                aria-label='Open shipment detail'
                onClick={goToShipmentDetail}
                style={{
                  fontWeight: '700',
                  cursor: 'pointer',
                  fontSize: '16px',
                  lineHeight: '16px',
                }}
              >
                <svg
                  aria-hidden='true'
                  width='16'
                  height='16'
                  viewBox='0 0 492.426 492.426'
                  style={{
                    marginTop: '-2px',
                    transform: 'rotate(130deg)',
                  }}
                >
                  <polygon
                    fill='var(--text-primary)'
                    points='492.426,246.213 406.213,160 406.213,231.213 86.213,231.213 86.213,160 0,246.213 86.213,332.427 86.213,261.213 406.213,261.213 406.213,332.427'
                  />
                </svg>
              </button>
              <button
                type='button'
                className='shipment-data-card-action'
                aria-label='Close shipment detail'
                onClick={() => closeModal()}
              >
                <CrossIcon color='var(--text-primary)' />
              </button>
            </div>
          </Flex>

          {timeToAct && !compact && (
            <div
              data-testid='time-to-act-banner'
              className={`time-to-act-banner time-to-act-${timeToAct.urgency}`}
            >
              {timeToAct.label}
            </div>
          )}

          {alertsData?.length > 0 &&
            alertsData.map((alert, index) => {
              const isBoundCall =
                alert?.alert_type === 'in_bound' ||
                alert?.alert_type === 'out_bound';
              const is_gxp = alert?.alert_type === 'temperature_gxp';
              const colors = getAlertColors(alert, isBoundCall, is_gxp);
              return (
                <React.Fragment key={alert.id}>
                  {alertIndex === index && (
                    <>
                      <Flex vertical gap={6}>
                        {alertsData.length > 1 && (
                          <>
                            <AlertsNavButtons
                              alertsArrayLength={alertsData.length}
                              alertIndex={alertIndex}
                              setAlertIndex={setAlertIndex}
                              index={index}
                            />
                          </>
                        )}

                        <StatusCardNotification
                          cardStyle={'style-1'}
                          icon={<WarningFilled />}
                          title={alert?.title || ''}
                          description={alert?.detail || ''}
                          createdAt={alert?.created_at || ''}
                          bgColor={colors.bgColor}
                          color={colors.color}
                          actionButtonBgColor={colors.actionButtonBgColor}
                          showButtons={false}
                          actionButtonBorderRadius='8px'
                        />
                      </Flex>
                    </>
                  )}
                </React.Fragment>
              );
            })}

          {!showTripRows ? (
            <CustomShipmentText
              text={[
                shipmentData?.courier?.title,
                shipmentData?.courier_tracking_id,
              ]
                .filter(Boolean)
                .join(' · ')}
              fontSize='12px'
              fontWeight={400}
              lineHeight='16px'
              color='var(--text-light)'
              marginBottom='0'
              whiteSpace='nowrap'
              ellipsis
            />
          ) : null}

          <Flex vertical={true} gap={16}>
            {showTripRows && (
              <>
                <Row justify='space-between'>
                  <Col span={11}>
                    <CustomShipmentText
                      text={'Delivery status'}
                      fontWeight={600}
                      lineHeight='16px'
                      color='var(--text-primary)'
                      marginBottom='4px'
                    />

                    <Tag
                      style={buildStatusPillStyle(deliveryStatusColors, {
                        borderRadius: '8px',
                        fontSize: '10px',
                      })}
                    >
                      {shipmentData?.delivery_status}
                    </Tag>
                  </Col>

                  <Col
                    title={
                      // @ts-expect-error: shipmentData.type should always be a string - but I might be wrong
                      shipmentData?.type === 3 || shipmentData?.type === '3'
                        ? shipmentData?.actual_delivery_date
                        : shipmentData?.estimated_delivery_date
                    }
                    span={11}
                  >
                    <CustomShipmentText
                      text={
                        // @ts-expect-error: shipmentData.type should always be a string - but I might be wrong
                        shipmentData?.type === 3 || shipmentData?.type === '3'
                          ? 'Delivered on'
                          : 'Zoomlogi ETA'
                      }
                      fontWeight={600}
                      lineHeight='20px'
                      color='var(--text-primary)'
                      marginBottom='4px'
                    />
                    <CustomShipmentText
                      text={truncateByLength(
                        // @ts-expect-error: shipmentData.type should always be a string - but I might be wrong
                        shipmentData?.type === 3 || shipmentData?.type === '3'
                          ? shipmentData?.actual_delivery_date
                          : shipmentData?.estimated_delivery_date,
                        23
                      )}
                      fontSize='12px'
                      fontWeight={400}
                      lineHeight='16px'
                      color='var(--text-light)'
                      marginBottom='0'
                    />
                  </Col>
                </Row>
                <Row justify='space-between'>
                  <Col title={shipmentData?.to_location?.address} span={11}>
                    <CustomShipmentText
                      text={'Destination'}
                      fontWeight={600}
                      lineHeight='16px'
                      color='var(--text-primary)'
                      marginBottom='4px'
                    />
                    <CustomShipmentText
                      text={shipmentData?.to_location?.address}
                      lineHeight='20px'
                      color='var(--text-light)'
                      marginBottom='0'
                    />
                  </Col>

                  <Col title={shipmentData?.scheduled_delivery_date} span={11}>
                    <CustomShipmentText
                      text={'Scheduled delivery'}
                      fontWeight={600}
                      lineHeight='16px'
                      color='var(--text-primary)'
                      marginBottom='4px'
                    />
                    <CustomShipmentText
                      text={shipmentData?.scheduled_delivery_date}
                      fontSize='12px'
                      fontWeight={400}
                      lineHeight='16px'
                      color='var(--text-light)'
                      marginBottom='0'
                    />
                  </Col>
                </Row>

                <Row justify='space-between'>
                  <Col title={shipmentData?.courier?.title} span={11}>
                    <CustomShipmentText
                      text={`${theme.themeType === 2 ? 'Carrier' : 'Transport provider'}`}
                      fontWeight={600}
                      lineHeight='16px'
                      color='var(--text-primary)'
                      marginBottom='4px'
                    />
                    <CustomShipmentText
                      text={truncateByLength(shipmentData?.courier?.title, 23)}
                      fontSize='12px'
                      fontWeight={400}
                      lineHeight='16px'
                      color='var(--text-light)'
                      marginBottom='0'
                    />
                  </Col>

                  <Col title={shipmentData?.courier_tracking_id} span={11}>
                    <CustomShipmentText
                      text={`${shipmentData?.courier?.title || ''} tracking ID`}
                      fontWeight={600}
                      lineHeight='16px'
                      color='var(--text-primary)'
                      marginBottom='4px'
                    />
                    <CustomShipmentText
                      text={truncateByLength(
                        shipmentData?.courier_tracking_id,
                        23
                      )}
                      fontSize='12px'
                      fontWeight={400}
                      lineHeight='16px'
                      color='var(--text-light)'
                      marginBottom='0'
                    />
                  </Col>
                </Row>
              </>
            )}

            {!compact && (
              <>
                <Divider style={{ margin: '0' }} />

                <Row justify='space-between'>
                  <Col title={shipmentData?.product?.name} span={11}>
                    <CustomShipmentText
                      text={'Product'}
                      fontWeight={600}
                      lineHeight='16px'
                      color='var(--text-primary)'
                      marginBottom='4px'
                    />
                    <CustomShipmentText
                      text={truncateByLength(shipmentData?.product?.name, 23)}
                      fontSize='12px'
                      fontWeight={400}
                      lineHeight='16px'
                      color='var(--text-light)'
                      marginBottom='0'
                    />
                  </Col>

                  <Col
                    title={String(shipmentData?.product?.quantity)}
                    span={11}
                  >
                    <CustomShipmentText
                      text={'Product quantity'}
                      fontWeight={600}
                      lineHeight='16px'
                      color='var(--text-primary)'
                      marginBottom='4px'
                    />
                    <CustomShipmentText
                      text={truncateByLength(
                        String(shipmentData?.product?.quantity),
                        23
                      )}
                      fontSize='12px'
                      fontWeight={400}
                      lineHeight='16px'
                      color='var(--text-light)'
                      marginBottom='0'
                    />
                  </Col>
                </Row>

                <Row justify='space-between'>
                  <Col title={shipmentData?.product?.project_id} span={11}>
                    <CustomShipmentText
                      text={'Project ID'}
                      fontWeight={600}
                      lineHeight='16px'
                      color='var(--text-primary)'
                      marginBottom='4px'
                    />
                    <CustomShipmentText
                      text={truncateByLength(
                        shipmentData?.product?.project_id,
                        23
                      )}
                      fontSize='12px'
                      fontWeight={400}
                      lineHeight='16px'
                      color='var(--text-light)'
                      marginBottom='0'
                    />
                  </Col>

                  <Col
                    title={shipmentData?.product?.temperature_range}
                    span={11}
                  >
                    <CustomShipmentText
                      text={'Temperature range'}
                      fontWeight={600}
                      lineHeight='16px'
                      color='var(--text-primary)'
                      marginBottom='4px'
                    />
                    <CustomShipmentText
                      text={truncateByLength(
                        shipmentData?.product?.temperature_range,
                        23
                      )}
                      fontSize='12px'
                      fontWeight={400}
                      lineHeight='16px'
                      color='var(--text-light)'
                      marginBottom='0'
                    />
                  </Col>
                </Row>

                <Row justify='space-between'>
                  <Col title={shipmentData?.sensor?.type} span={11}>
                    <CustomShipmentText
                      text={'Sensor'}
                      fontWeight={600}
                      lineHeight='16px'
                      color='var(--text-primary)'
                      marginBottom='4px'
                    />
                    <CustomShipmentText
                      text={truncateByLength(shipmentData?.sensor?.type, 23)}
                      fontSize='12px'
                      fontWeight={400}
                      lineHeight='16px'
                      color='var(--text-light)'
                      marginBottom='0'
                    />
                  </Col>

                  <Col title={shipmentData?.sensor?.id} span={11}>
                    <CustomShipmentText
                      text={'Sensor ID'}
                      fontWeight={600}
                      lineHeight='16px'
                      color='var(--text-primary)'
                      marginBottom='4px'
                    />
                    <CustomShipmentText
                      text={truncateByLength(shipmentData?.sensor?.id, 23)}
                      fontSize='12px'
                      fontWeight={400}
                      lineHeight='16px'
                      color='var(--text-light)'
                      marginBottom='0'
                    />
                  </Col>
                </Row>
                {/* shipments Vitals  */}
                <div className='mb-5'>
                  {shipmentData?.can_view_vitals && (
                    <ShipmentVitals_CT
                      shipment={shipmentData}
                      openCharts={false}
                    />
                  )}
                </div>
              </>
            )}
          </Flex>
        </Flex>
      </div>
    </div>
  );
};

export default HomeSingleShipmentData;
